import { Component } from 'react';
import PropTypes from 'prop-types';
import mint from '@tunein/mint';
import { connect } from 'react-redux';
import areGdprObligationsComplete from '../../components/consent/utils/areGdprObligationsComplete';

function mapStateToProps(state) {
  return {
    isMintInitialized: state.mint.isInitialized,
    gdprObligationsComplete: areGdprObligationsComplete(state),
  };
}

/**
 * Factory that creates a HOC to manage ad slot registration/requests in Mint
 *
 * @param {object} options
 *  @property {boolean} autoPlay - determines whether slots should be requested by the HOC or by an
 *    external process
 *  @property {boolean} refreshOnPageChange - determines whether slots should be refreshed when the
 *    user navigates to a different page
 */
export default function withAdRegistration(options) {
  const { autoPlay, refreshOnPageChange } = options;

  /**
   * HOC that manages ad slot registration/requests in Mint.
   *
   * @param {string} slotName - the name of the slot, as defined by the ad config
   *  (store.config.ads.adConfig)
   * @param {string} id - the unique ID of the HTML element where the ad will render
   * @param {string} [dimensions] - a string representing the specific dimensions required for this
   *  ad slot (e.g., "320x50")
   * @param {string} matchUrl - used to determine page change. Defined in
   *  withCurrentRouteCustomProps.js
   * @param {boolean} deregisterDisplaySlot - deregisters display ads when specific dialogs open.
   *  See store.mint.deregisterDisplayAds
   * @param {boolean} isMintInitialized - triggered by WithAds.js after Mint initializes
   * @param {boolean} gdprObligationsComplete - true when GDPR is determined to be false or when
   *  user explicitly selects consent preferences
   */
  return function withAdRegistrationInner(BaseComponent) {
    class WithAdRegistration extends Component {
      static propTypes = {
        slotName: PropTypes.string.isRequired,
        id: PropTypes.string.isRequired,
        dimensions: PropTypes.string,
        matchUrl: PropTypes.string,
        deregisterDisplaySlot: PropTypes.bool.isRequired,
        handleAdDeregistration: PropTypes.func,

        // mapStateToProps
        isMintInitialized: PropTypes.bool,
        gdprObligationsComplete: PropTypes.bool.isRequired,
      };

      static defaultProps = {
        handleAdDeregistration: () => {},
      };

      componentDidMount() {
        if (this.props.isMintInitialized) {
          this.registerAndRequestSlot();
        }
      }

      componentDidUpdate(prevProps) {
        const {
          matchUrl,
          isMintInitialized,
          deregisterDisplaySlot,
          gdprObligationsComplete,
        } = this.props;

        if (!isMintInitialized) {
          return;
        }

        if (
          (!prevProps.isMintInitialized && gdprObligationsComplete) ||
          (gdprObligationsComplete && !prevProps.gdprObligationsComplete) ||
          (refreshOnPageChange && matchUrl !== prevProps.matchUrl) ||
          (!deregisterDisplaySlot && prevProps.deregisterDisplaySlot)
        ) {
          this.registerAndRequestSlot();
          return;
        }

        if (deregisterDisplaySlot && !prevProps.deregisterDisplaySlot) {
          this.deregisterSlot();
        }
      }

      componentWillUnmount() {
        this.deregisterSlot();
      }

      registerAndRequestSlot() {
        const { id, slotName, dimensions, gdprObligationsComplete } =
          this.props;

        if (!gdprObligationsComplete) {
          return;
        }

        mint.registerSlot(id, slotName, dimensions);

        if (autoPlay) {
          mint.requestSlot(id);
        }
      }

      deregisterSlot() {
        mint.deregisterSlot(this.props.id);
        this.props.handleAdDeregistration();
      }

      render() {
        return <BaseComponent {...this.props} />;
      }
    }

    return connect(mapStateToProps)(WithAdRegistration);
  };
}
